'use client';

import { ComponentRef, forwardRef, useEffect, useRef, useState } from 'react';
import MapView from '@/app/__to_be_integrated/_____components/map/MapView';
import type { MapViewProps } from "@/app/__to_be_integrated/_____components/map/MapView";
import { Box } from '@mui/material';
import { MapProps } from "@vis.gl/react-google-maps";
import FitBounds from "@/app/__to_be_integrated/_____components/map/helper/fit-bound";
import { LatLngLiteral } from "@googlemaps/google-maps-services-js";

// Fractions for each side of the viewport/container: 0..1 (e.g., 1/3 => 0.333...)
export interface OverlayFractions {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
}

export interface FocusMapProps extends MapProps {
    overlay?: OverlayFractions;
    fitPoints?: LatLngLiteral[];
    lockInPlace?: boolean;
    consentPosition?: MapViewProps["consentPosition"];
}

const FocusMap = forwardRef<ComponentRef<typeof MapView>, FocusMapProps>(
    ({ children, overlay, fitPoints, lockInPlace, consentPosition, ...props }, ref) => {

        const containerRef = useRef<HTMLDivElement | null>(null);
        const [paddingPx, setPaddingPx] = useState({ left: 0, right: 0, top: 0, bottom: 0 });

        useEffect(() => {
            if (!containerRef.current) return;

            const update = () => {
                const w = containerRef.current!.clientWidth;
                const h = containerRef.current!.clientHeight;

                setPaddingPx({
                    left: Math.round((overlay?.left ?? 0) * w),
                    right: Math.round((overlay?.right ?? 0) * w),
                    top: Math.round((overlay?.top ?? 0) * h),
                    bottom: Math.round((overlay?.bottom ?? 0) * h),
                });
            };

            update();

            const ro = new ResizeObserver(update);
            ro.observe(containerRef.current);
            return () => ro.disconnect();
        }, [overlay]);

        return (
            <Box ref={containerRef} sx={{ width: '100%', height: '100%' }}>
                <MapView
                    ref={ref}
                    key={`pad-${paddingPx.left}-${paddingPx.right}-${paddingPx.top}-${paddingPx.bottom}`}
                    consentPosition={consentPosition}
                    {...props}

                >
                    {fitPoints && (
                        <FitBounds
                            points={fitPoints}
                            lockInPlace={lockInPlace}
                            padding={{
                                left: paddingPx.left,
                                right: paddingPx.right,
                                top: paddingPx.top,
                                bottom: paddingPx.bottom,
                            }}
                            overlayPadding={{
                                left: paddingPx.left,
                                right: paddingPx.right,
                                top: paddingPx.top,
                                bottom: paddingPx.bottom,
                            }}
                        />
                    )}
                    {children}
                </MapView>
            </Box>
        );
    }
)

FocusMap.displayName = "FocusMap";
export default FocusMap;
